import {
    Button,
    Flex,
    FormControl,
    FormLabel,
    Grid,
    Input,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    Text,
    useColorModeValue,
    FormErrorMessage,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const DocumentsTypeModal = ({ isOpen, onClose, selectedType, documentTypes, onSave }) => {
    const { t } = useTranslation();
    const textColor = useColorModeValue("gray.700", "white");
    const [type, setType] = useState("");
    const [description, setDescription] = useState("");
    const [typeError, setTypeError] = useState(undefined);

    useEffect(() => {
        const initialType = selectedType?.tipo || "";
        setType(initialType);
        setDescription(selectedType?.descricao || "");
        setTypeError(undefined);
    }, [selectedType, isOpen]);

    const validateType = (value) => {
        if (!value.trim()) {
            return t("system.messages.errors.required-field");
        }
        if (value.length > 400) {
            return t("system.messages.errors.max-length", { max: 400 });
        }

        const duplicatedType = documentTypes.some(
            (doc) => doc.tipo.toLowerCase() === value.trim().toLowerCase() &&
                (!selectedType || doc.id !== selectedType.id)
        );

        if (duplicatedType) {
            return t("document-type.messages.errors.duplicated-type", { field: t("document-type.form.fields.type.label") });
        }

        return undefined;
    };

    const handleTypeChange = (e) => {
        const value = e.target.value;
        setType(value);
        setTypeError(validateType(value));
    };

    const isSaveDisabled = !!typeError || !type.trim();

    const handleSubmitType = async () => {
        const typeData = {
            tipo: type.trim(),
            descricao: description.trim(),
        };
        onSave(typeData);
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <Flex direction="column">
                        <Text
                            color={textColor}
                            fontSize="lg"
                            fontWeight="bold"
                        >
                            {selectedType ? t("document-type.form.edit-title") : t("document-type.form.create-title")}
                        </Text>
                    </Flex>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <Grid templateColumns={{ sm: "1fr", md: "repeat(2, 1fr)" }} gap="24px">
                        <FormControl isRequired isInvalid={!!typeError}>
                            <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                {t("document-type.form.fields.type.label")}
                            </FormLabel>
                            <Input
                                value={type}
                                onChange={handleTypeChange}
                                placeholder={t("document-type.form.fields.type.placeholder")}
                                fontSize="sm"
                                required
                            />
                            <FormErrorMessage>{typeError}</FormErrorMessage>
                        </FormControl>
                        <FormControl>
                            <FormLabel
                                color={textColor}
                                fontWeight="bold"
                                fontSize="xs"
                            >
                                {t("document-type.form.fields.description.label")}
                            </FormLabel>
                            <Input
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder={t("document-type.form.fields.description.placeholder")}
                                fontSize="sm"
                            />
                        </FormControl>
                    </Grid>
                    <Flex w="100%" justify="space-between" mb={4} mt={4}>
                        <Button
                            variant="no-effects"
                            alignSelf="flex-end"
                            w="125px"
                            bg="gray.100"
                            color="gray.700"
                            fontSize="sm"
                            fontWeight="bold"
                            onClick={onClose}
                        >
                            {t("system.actions.cancel")}
                        </Button>
                        <Button
                            w="100px"
                            variant="dark"
                            fontSize="sm"
                            type="submit"
                            disabled={isSaveDisabled}
                            _hover={{
                                bg: "grey.500",
                                color: "white"
                            }}
                            onClick={handleSubmitType}
                        >
                            {t(selectedType ? 'system.actions.edit' : 'system.actions.save')}
                        </Button>
                    </Flex>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default DocumentsTypeModal;
